import type { Metadata } from 'next';
import { i18n, type Locale } from '~/i18n/i18n-config';

/** Route segments are canonical English, so a path is normally the same string for every locale.
 * The callback form exists for the two cases where it is not: returning `null` skips a locale
 * (hreflang presence), and a per-locale path can carry locale-specific query params. */
type PathnameInput = string | ((locale: Locale) => string | null | undefined);

export const CONTENT_LOCALES_META = 'byway:content-locales';

/**
 * Builds hreflang alternate links for search engines.
 * Used in per-page `generateMetadata` to signal locale variants.
 * Each locale self-canonicalises — the canonical points to the current page's locale variant,
 * Pass `locale` from `[lang]` pages; defaults to `en-GB`.
 */
export async function buildAlternates(
  pathname: PathnameInput,
  options?: { locale?: Locale; contentLocales?: readonly Locale[] }
): Promise<Metadata['alternates']> {
  const currentLocale = options?.locale ?? i18n.defaultLocale;

  const contentLocales = options?.contentLocales;
  const eligibleLocales = contentLocales
    ? i18n.liveLocales.filter((locale) => contentLocales.includes(locale))
    : i18n.liveLocales;

  const entries = eligibleLocales.map((locale) => {
    const resolvedPath = typeof pathname === 'function' ? pathname(locale) : pathname;

    if (resolvedPath == null) return null;

    const isDefault = locale === i18n.defaultLocale;
    const url = isDefault ? resolvedPath : `/${locale}${resolvedPath === '/' ? '' : resolvedPath}`;

    return [locale, url] as const;
  });

  const languages: Record<string, string> = Object.fromEntries(
    entries.filter((entry): entry is NonNullable<typeof entry> => entry !== null)
  );

  const xDefault = languages[i18n.defaultLocale] ?? Object.values(languages)[0];
  if (xDefault) languages['x-default'] = xDefault;

  return {
    canonical: languages[currentLocale],
    languages
  };
}
